import { createSlice, isAnyOf, PayloadAction } from "@reduxjs/toolkit"
import {
    ControlSliceState,
    LightningUnit,
    OverlayKey,
    PartialViewState,
    PrecipUnit,
    WeatherMode,
    WeatherPreferences,
    WeatherState,
    WindUnit,
} from "./types"
import { LayerStatus } from "../layers/overlays/layerStatus"

const HOUR_MS = 60 * 60 * 1000
const computeDefaultWeatherTimeline = (mode: WeatherMode): WeatherState["timeline"] => {
    const now = Date.now()
    return {
        currentTime: new Date(
            mode === "lightning" ? now : Math.floor(now / HOUR_MS) * HOUR_MS
        ).toISOString(),
        isPlaying: false,
        playbackSpeed: 1,
    }
}
import { RegionIds } from "configuration/locations"
import {
    zoomToShowFirePerimeter,
    zoomToShowFlightPath,
    zoomToShowHotspotsInFlightPath,
    zoomToShowPoints,
    zoomToShowText,
} from "features/map/zoom/thunks"
import { calculatePreferredViewStateFromBoundaryBox, getMapViewStateFromRegion } from "../module"
import { activateFlightPath } from "../layers/slice"
import { ToolAttributes } from "@nova/constants"
import { Icon } from "@nova/protobuf"
import { updateIcon } from "features/pubsub/entityActions/cubes/icon"
import { setActiveTool } from "plugins/Toolbox/tool-registry"
import { loadAndZoomProject, loadProject } from "./thunks"
import { resetViewstate, setNewViewstate } from "../GlobalViewState"
import {
    addLayerToSelection,
    clearSelection,
    removeLayerFromSelection,
    selectSingleLayer,
} from "panels/content/side-panel/slice.new"

const initialViewState = getMapViewStateFromRegion(RegionIds.NorthAmerica)

export const weatherInitialState: WeatherState = {
    enabled: false,
    timeline: computeDefaultWeatherTimeline("wind"),
    panel: {
        mode: "wind",
        opacity: 0.75,
        particleEffect: true,
        groundStrikes: true,
        flashStrikes: true,
        detailedAreas: false,
    },
    preferences: {
        windUnit: WindUnit.Knots,
        precipUnit: PrecipUnit.Millimeters,
        lightningUnit: LightningUnit.PerSquareKilometerHour,
    },
}

const initialState: ControlSliceState = {
    entities: {
        fireId: undefined,
        flightId: undefined,
        hotspotId: undefined,
        firePerimeterId: undefined,
        selectedMapIcon: undefined,
        clipboardMapIcon: undefined,
    },
    history: {
        chapter: [],
        viewStates: {},
    },
    viewState: initialViewState,
    controllerOverrides: {},
    mapFeatures: {
        mapLabels: true,
        contours: true,
        shading: false,
        buildings: false,
    },
    layerDropdownOpen: false,
    weather: { ...weatherInitialState },
    overlayStatus: {},
    overlaySceneTimes: {},
}

const mapFeaturesLocalStorageKey = "mapFeatures"
const weatherPreferencesLocalStorageKey = "weatherPreferences"

const persistWeatherPreferences = (preferences: WeatherPreferences) => {
    try {
        localStorage.setItem(weatherPreferencesLocalStorageKey, JSON.stringify(preferences))
    } catch {}
}

export const controlSlice = createSlice({
    name: "map/control",
    initialState: { ...initialState },
    reducers: {
        setFlightId: (state: ControlSliceState, action: PayloadAction<string>) => {
            state.entities.flightId = action.payload
        },
        setSelectedMapIcon: (state: ControlSliceState, action: PayloadAction<Icon | undefined>) => {
            state.entities.selectedMapIcon = action.payload
        },
        setClipboardMapIcon: (
            state: ControlSliceState,
            action: PayloadAction<ToolAttributes | undefined>
        ) => {
            state.entities.clipboardMapIcon = action.payload
        },
        clearSelectedMapIcon: (state: ControlSliceState) => {
            state.entities.selectedMapIcon = undefined
        },
        clearClipboardMapIcon: (state: ControlSliceState) => {
            state.entities.clipboardMapIcon = undefined
        },
        zoomToArea: (_: ControlSliceState, action: PayloadAction<Partial<PartialViewState>>) => {
            setNewViewstate(action.payload, { disableFly: true })
        },
        clearMapViewState: (state: ControlSliceState) => {
            state.viewState = undefined
        },
        resetMapToInitialRegion: (state: ControlSliceState) => {
            state.viewState = initialViewState
        },
        customMapController: (state: ControlSliceState, action) => {
            const { options } = action.payload
            state.controllerOverrides = options
        },
        enabledMapController: (state: ControlSliceState) => {
            state.controllerOverrides = {}
        },
        disabledMapController: (state: ControlSliceState) => {
            state.controllerOverrides = { dragPan: false, dragRotate: false }
        },
        setPictureInPicture: (state: ControlSliceState, action: PayloadAction<boolean>) => {
            if (action.payload) {
                state.isPictureInPicture = true
                state.controllerOverrides = { dragPan: false, dragRotate: false }
            } else {
                state.isPictureInPicture = false
                state.controllerOverrides = {}
            }
        },
        setMapLabels: (state: ControlSliceState, action: PayloadAction<boolean>) => {
            state.mapFeatures.mapLabels = action.payload
            localStorage.setItem(mapFeaturesLocalStorageKey, JSON.stringify(state.mapFeatures))
        },
        setContours: (state: ControlSliceState, action: PayloadAction<boolean>) => {
            state.mapFeatures.contours = action.payload
            localStorage.setItem(mapFeaturesLocalStorageKey, JSON.stringify(state.mapFeatures))
        },
        setShading: (state: ControlSliceState, action: PayloadAction<boolean>) => {
            state.mapFeatures.shading = action.payload
            localStorage.setItem(mapFeaturesLocalStorageKey, JSON.stringify(state.mapFeatures))
        },
        setBuildings: (state: ControlSliceState, action: PayloadAction<boolean>) => {
            state.mapFeatures.buildings = action.payload
            localStorage.setItem(mapFeaturesLocalStorageKey, JSON.stringify(state.mapFeatures))
        },
        setWeather: (state: ControlSliceState, action: PayloadAction<boolean>) => {
            state.weather.enabled = action.payload
            localStorage.setItem(mapFeaturesLocalStorageKey, JSON.stringify(state.mapFeatures))
            if (action.payload) {
                state.weather.timeline = computeDefaultWeatherTimeline(state.weather.panel.mode)
            }
        },
        setWeatherMode: (state: ControlSliceState, action: PayloadAction<WeatherMode>) => {
            state.weather.panel.mode = action.payload
            state.weather.timeline = {
                ...computeDefaultWeatherTimeline(action.payload),
                currentTime: state.weather.timeline.currentTime,
            }
        },
        setWeatherCurrentTime: (state: ControlSliceState, action: PayloadAction<string>) => {
            state.weather.timeline.currentTime = action.payload
        },
        setWeatherIsPlaying: (state: ControlSliceState, action: PayloadAction<boolean>) => {
            state.weather.timeline.isPlaying = action.payload
        },
        setWeatherPlaybackSpeed: (state: ControlSliceState, action: PayloadAction<number>) => {
            state.weather.timeline.playbackSpeed = action.payload
        },
        setWeatherOpacity: (state: ControlSliceState, action: PayloadAction<number>) => {
            state.weather.panel.opacity = action.payload
        },
        initWeatherPreferences: (
            state: ControlSliceState,
            action: PayloadAction<WeatherPreferences>
        ) => {
            state.weather.preferences = action.payload
        },
        setWeatherParticleEffect: (state: ControlSliceState, action: PayloadAction<boolean>) => {
            // Panel state — redux is the source of truth; intentionally not persisted.
            state.weather.panel.particleEffect = action.payload
        },
        setWeatherGroundStrikes: (state: ControlSliceState, action: PayloadAction<boolean>) => {
            state.weather.panel.groundStrikes = action.payload
        },
        setWeatherFlashStrikes: (state: ControlSliceState, action: PayloadAction<boolean>) => {
            state.weather.panel.flashStrikes = action.payload
        },
        setWeatherDetailedAreas: (state: ControlSliceState, action: PayloadAction<boolean>) => {
            state.weather.panel.detailedAreas = action.payload
        },
        setWeatherWindUnit: (state: ControlSliceState, action: PayloadAction<WindUnit>) => {
            state.weather.preferences.windUnit = action.payload
            persistWeatherPreferences(state.weather.preferences)
        },
        setWeatherPrecipUnit: (state: ControlSliceState, action: PayloadAction<PrecipUnit>) => {
            state.weather.preferences.precipUnit = action.payload
            persistWeatherPreferences(state.weather.preferences)
        },
        setWeatherLightningUnit: (
            state: ControlSliceState,
            action: PayloadAction<LightningUnit>
        ) => {
            state.weather.preferences.lightningUnit = action.payload
            persistWeatherPreferences(state.weather.preferences)
        },
        setOverlayStatus: (
            state: ControlSliceState,
            action: PayloadAction<{ key: OverlayKey; status: LayerStatus }>
        ) => {
            state.overlayStatus[action.payload.key] = action.payload.status
        },
        setOverlaySceneTime: (
            state: ControlSliceState,
            action: PayloadAction<{ key: OverlayKey; sceneTime: number | undefined }>
        ) => {
            if (action.payload.sceneTime === undefined) {
                delete state.overlaySceneTimes[action.payload.key]
                return
            }
            state.overlaySceneTimes[action.payload.key] = action.payload.sceneTime
        },
        resetBearing: () => {
            setNewViewstate({ bearing: 0, pitch: 0 }, { disableFly: true })
        },
        deactivateFire: (state: ControlSliceState) => {
            state.entities.chapterId = undefined
            state.entities.fireId = undefined
            state.entities.flightId = undefined
            state.entities.firePerimeterId = undefined
        },
        zoomToShowPoint: (
            state: ControlSliceState,
            action: {
                payload: { lng: number; lat: number; zoomLevel?: number; preserveZoom?: boolean }
            }
        ) => {
            const { requiresUpdate, ...newViewState } = calculatePreferredViewStateFromBoundaryBox(
                state.viewState as PartialViewState,
                {
                    maxLongitude: action.payload.lng,
                    maxLatitude: action.payload.lat,
                    minLatitude: action.payload.lat,
                    minLongitude: action.payload.lng,
                },
                0,
                action.payload.zoomLevel,
                action.payload.preserveZoom
            )

            if (requiresUpdate) {
                setNewViewstate(newViewState)
            }
        },
        panToShowPoint: (
            _state: ControlSliceState,
            action: { payload: { lng: number; lat: number } }
        ) => {
            setNewViewstate({ longitude: action.payload.lng, latitude: action.payload.lat })
        },
        clearMapControlState: () => {
            return { ...initialState }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(updateIcon, (state, action) => {
                const selectedIcon = state.entities.selectedMapIcon as Icon
                state.entities.selectedMapIcon = { ...selectedIcon, ...action.payload.updates }
            })
            .addCase(activateFlightPath, (state, action) => {
                if (action.payload.autoZoom && action.payload.uploads && state.viewState) {
                    const { uploads } = action.payload
                    const latitudes = [] as number[]
                    const longitudes = [] as number[]
                    uploads.forEach((upload: { latitude: number; longitude: number }) => {
                        if (upload) {
                            latitudes.push(upload.latitude)
                            longitudes.push(upload.longitude)
                        }
                    })

                    const boundaryBox = {
                        minLatitude: Math.min(...latitudes) || 0,
                        minLongitude: Math.min(...longitudes) || 0,
                        maxLatitude: Math.max(...latitudes) || 0,
                        maxLongitude: Math.max(...longitudes) || 0,
                    }
                    if (Object.values(boundaryBox).some((e) => !e)) return
                    const { requiresUpdate, ...newViewState } =
                        calculatePreferredViewStateFromBoundaryBox(state.viewState, boundaryBox)

                    if (requiresUpdate) {
                        setNewViewstate(newViewState)
                    }
                }
            })
            .addCase(zoomToShowFlightPath.fulfilled, (state, action) => {
                if (!state.viewState) return
                const boundaryBox = action.payload
                if (!boundaryBox) return
                if (Object.values(boundaryBox).some((e) => !e)) return
                const { requiresUpdate, ...newViewState } =
                    calculatePreferredViewStateFromBoundaryBox(state.viewState, boundaryBox)

                if (requiresUpdate) {
                    setNewViewstate(newViewState)
                }
            })
            .addCase(zoomToShowText.fulfilled, (state, action) => {
                if (!state.viewState) return
                const { point, zoomLevel } = action.payload
                const { requiresUpdate, ...newViewState } =
                    calculatePreferredViewStateFromBoundaryBox(
                        state.viewState,
                        {
                            maxLongitude: point[0],
                            maxLatitude: point[1],
                            minLatitude: point[1],
                            minLongitude: point[0],
                        },
                        300,
                        zoomLevel
                    )

                if (requiresUpdate) {
                    setNewViewstate(newViewState)
                }
            })
            .addCase(loadAndZoomProject.pending, (state, action) => {
                state.entities.fireId = action.meta.arg.projectId
            })
            .addCase(loadAndZoomProject.fulfilled, (state, action) => {
                const projectViewState = action.payload
                if (!projectViewState) return
                setNewViewstate(
                    {
                        ...projectViewState,
                        pitch: 0,
                        bearing: 0,
                    },
                    { disableFly: true }
                )
            })
            .addCase(loadProject.pending, (state) => {
                resetViewstate()
                let mapFeatures = null
                try {
                    mapFeatures = JSON.parse(localStorage.getItem(mapFeaturesLocalStorageKey) || "")
                } catch {}
                if (mapFeatures && typeof mapFeatures === "object") {
                    for (const feature of Object.keys(state.mapFeatures)) {
                        if (feature in mapFeatures) {
                            state.mapFeatures[feature as keyof typeof state.mapFeatures] =
                                !!mapFeatures[feature]
                        }
                    }
                }

                state.weather = {
                    ...weatherInitialState,
                    panel: state.weather.panel,
                    preferences: state.weather.preferences,
                    timeline: computeDefaultWeatherTimeline(state.weather.panel.mode),
                }
            })
            .addMatcher(
                isAnyOf(
                    selectSingleLayer,
                    addLayerToSelection,
                    removeLayerFromSelection,
                    clearSelection,
                    setActiveTool
                ),
                (state) => {
                    state.isPictureInPicture = false
                }
            )
            .addMatcher(
                isAnyOf(
                    zoomToShowFirePerimeter.fulfilled,
                    zoomToShowHotspotsInFlightPath.fulfilled,
                    zoomToShowPoints.fulfilled
                ),
                (state, action) => {
                    if (!action.payload.boundaryBox || !state.viewState) {
                        return
                    }

                    const padding = "padding" in action.payload ? action.payload.padding : undefined
                    const preserveZoom =
                        "preserveZoom" in action.payload ? action.payload.preserveZoom : undefined

                    const { requiresUpdate, ...newViewState } =
                        calculatePreferredViewStateFromBoundaryBox(
                            state.viewState,
                            action.payload.boundaryBox,
                            padding,
                            undefined,
                            preserveZoom
                        )

                    if (requiresUpdate) {
                        setNewViewstate(newViewState)
                    }
                }
            )
    },
})

// Action creators are generated for each case reducer function
export const {
    setFlightId,
    zoomToArea,
    setClipboardMapIcon,
    clearSelectedMapIcon,
    resetMapToInitialRegion,
    disabledMapController,
    setPictureInPicture,
    setMapLabels,
    setContours,
    setShading,
    setBuildings,
    setWeather,
    setWeatherMode,
    setWeatherCurrentTime,
    setWeatherIsPlaying,
    setWeatherPlaybackSpeed,
    setWeatherOpacity,
    initWeatherPreferences,
    setWeatherParticleEffect,
    setWeatherGroundStrikes,
    setWeatherFlashStrikes,
    setWeatherDetailedAreas,
    setWeatherWindUnit,
    setWeatherPrecipUnit,
    setWeatherLightningUnit,
    setOverlayStatus,
    setOverlaySceneTime,
    resetBearing: resetBearingAction,
    customMapController,
    deactivateFire,
    zoomToShowPoint,
    panToShowPoint,
    enabledMapController,
    clearMapControlState,
} = controlSlice.actions

export default controlSlice.reducer
